// SPDX-License-Identifier: AGPL-3.0-or-later

import ExperimentAssignments from '@app/features/experiment/state/ExperimentAssignments';
import Relationships from '@app/features/relationship/state/Relationships';
import {serverNow} from '@app/features/voice_scene/net/RoomServerClock';
import {readVoiceRoomAssignment} from '@fluxer/schema/src/domains/experiment/ExperimentSchemas';
import {
	decodeVoiceRoomStageMetadata,
	type VoiceRoomStage,
} from '@fluxer/schema/src/domains/voice_seat/VoiceRoomStageSchemas';
import {type Room, RoomEvent} from 'livekit-client';
import {useEffect, useState} from 'react';

// The stage a bot runs in this room, from the room metadata the API writes (a late joiner gets it on connect). null
// while room_stage_enabled is off, for a bot this viewer blocked, and once its expires_at passes on the API's clock
// (one timer). Call from an observer: the switch and the block list are read as it renders.
export function useRoomStage(room: Room | null): VoiceRoomStage | null {
	const [stage, setStage] = useState<VoiceRoomStage | null>(null);
	useEffect(() => {
		if (!room) {
			setStage(null);
			return;
		}
		let raw = room.metadata;
		setStage(decodeVoiceRoomStageMetadata(raw));
		const onChange = (metadata: string) => {
			if (metadata === raw) return;
			raw = metadata;
			setStage(decodeVoiceRoomStageMetadata(metadata));
		};
		room.on(RoomEvent.RoomMetadataChanged, onChange);
		return () => {
			room.off(RoomEvent.RoomMetadataChanged, onChange);
		};
	}, [room]);
	const [lapsed, setLapsed] = useState<VoiceRoomStage | null>(null);
	useEffect(() => {
		if (!stage) return;
		const wait = stage.expires_at - serverNow();
		if (wait <= 0) {
			setLapsed(stage);
			return;
		}
		const timer = setTimeout(() => setLapsed(stage), wait);
		return () => clearTimeout(timer);
	}, [stage]);
	if (!stage || lapsed === stage || stage.expires_at <= serverNow()) return null;
	if (!readVoiceRoomAssignment(ExperimentAssignments.response).stage) return null;
	return Relationships.isBlocked(stage.bot.id) ? null : stage;
}
